import {
  IconSignal,
  Button,
  SequenceMenu,
  SettingsFormControl,
  SettingsFormControlIcon,
  useModalState,
  useNotifications,
  Modal,
  SimpleModal,
  Box
} from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNavigate } from 'react-router-dom'
import {
  getNotificationSummary,
  NotificationPolicy
} from './nofitications.utils'
import { WebhookSecretDetails } from './WebhookSecretDetails'

export const WebhookCard = ({ policy }: { policy: NotificationPolicy }) => {
  const navigate = useNavigate()
  const {
    isModalOpen: isSecretKeyModalOpen,
    openModal: openSecretKeyModal,
    closeModal: closeSecretKeyModal
  } = useModalState()
  const {
    isModalOpen: isDeleteDialogOpen,
    openModal: openDeleteDialog,
    closeModal: closeDeleteDialog
  } = useModalState()

  return (
    <>
      <SettingsFormControl
        key={policy.id}
        label={policy.name}
        htmlFor={`policy-${policy.id}`}
        helper={getNotificationSummary(policy.notificationTypes)}
        onClick={() => {
          navigate(`/settings/webhooks/${policy.id}`)
        }}
        icon={
          <SettingsFormControlIcon>
            <IconSignal />
          </SettingsFormControlIcon>
        }
      >
        <SequenceMenu
          variant="ghost"
          buttonSize="medium"
          data-testid="webhooks.card.menu"
          items={[
            {
              label: 'Edit',
              onClick: () => {
                navigate(`/settings/webhooks/${policy.id}`)
              }
            },
            {
              label: 'Show secret key',
              'data-testid': 'webhooks.card.showSecret',
              onClick: e => {
                e.stopPropagation()
                openSecretKeyModal()
              }
            },
            {
              label: 'Delete',
              style: { color: 'red' },
              onClick: e => {
                e.stopPropagation()
                openDeleteDialog()
              }
            }
          ]}
        />
      </SettingsFormControl>
      <SecretKeyModal
        webhookSecret={policy.webhookSecret}
        isOpen={isSecretKeyModalOpen}
        onClose={closeSecretKeyModal}
      />
      <DeleteModal
        name={policy.name}
        id={policy.id}
        isOpen={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
      />
    </>
  )
}

const DeleteModal = ({
  name,
  id,
  isOpen,
  onClose
}: {
  name: string
  id: string
  isOpen: boolean
  onClose: () => void
}) => {
  const { displayNotification } = useNotifications()

  const mutator = useMutation(
    dashboard20240730Client.deleteNotificationPolicy,
    {
      onSuccess() {
        displayNotification('Webhook deleted', {
          type: 'success'
        })
      },
      onError() {
        displayNotification('Webhook failed to delete ', {
          type: 'error'
        })
      }
    }
  )

  return (
    <SimpleModal
      variant="dialog"
      data-testid="webhooks.deleteDialog"
      isOpen={isOpen}
      onClose={onClose}
      title="Delete webhook"
      submitButtonText="Delete"
      submittingButtonText="Deleting"
      isDestructive
      disableSubmitOnEnter
      onSubmit={async () => {
        try {
          await mutator.mutateAsync({ id })
        } catch {
          return false
        }
        return true
      }}
    >
      Are you sure? This will permanently delete the <b>{name}</b> webhook.
    </SimpleModal>
  )
}

const SecretKeyModal = ({
  webhookSecret,
  isOpen,
  onClose
}: {
  webhookSecret?: string
  isOpen: boolean
  onClose: () => void
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      data-testid="webhooks.secretKeyModal"
    >
      <Modal.Header withBorder>Secret key</Modal.Header>

      <Modal.Content minHeight="auto">
        <WebhookSecretDetails webhookSecret={webhookSecret} />
      </Modal.Content>

      <Modal.Footer withBorder>
        <Button
          variant="primary"
          data-testid="webhooks.secretKeyModal.done"
          onClick={onClose}
        >
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
